import { TIERS, quote } from './wholesale-pricing.js';

// Browser storage only remembers that this device entered the wholesale password (30 days).
const TOKEN_KEY = 'rw_wholesale_token';
const MAX_FILES = 5;
const MAX_FILE_MB = 20;

const money = (n) =>
  `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const root = () => window.Shopify?.routes?.root ?? '/';

const store = {
  get(key) {
    try {
      return JSON.parse(localStorage.getItem(key));
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* private mode: the page still works, it just won't remember */
    }
  },
  remove(key) {
    try {
      localStorage.removeItem(key);
    } catch {}
  },
};

async function cartFetch(path, options = {}) {
  const isForm = options.body instanceof FormData;
  const res = await fetch(`${root()}${path}`, {
    ...options,
    headers: isForm ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.description || data.message || `Cart error (${res.status})`);
  return data;
}

class WholesaleOrder extends HTMLElement {
  connectedCallback() {
    this.data = JSON.parse(this.querySelector('[data-wholesale-data]').textContent);
    this.worker = (this.dataset.worker || '').replace(/\/$/, '');
    this.qty = { tops: {}, bottoms: {} };
    this.files = [];

    // Back from checkout restores this page from the browser cache with the
    // button still reading "Opening checkout…". Reset it.
    if (!this.pageshowBound) {
      this.pageshowBound = true;
      window.addEventListener('pageshow', (e) => {
        if (!e.persisted || !this.ready) return;
        this.busy = false;
        this.querySelector('[data-checkout]').textContent = 'Checkout';
        this.update();
      });
    }

    this.querySelector('[data-gate]').addEventListener('submit', (e) => {
      e.preventDefault();
      this.unlock();
    });

    const token = store.get(TOKEN_KEY);
    if (this.hasAttribute('data-design-mode') || (token && Number(String(token).split('.')[0]) > Date.now())) {
      this.showSheet();
    }
  }

  // --- Password gate ---------------------------------------------------------

  async unlock() {
    const input = this.querySelector('[data-password]');
    const error = this.querySelector('[data-gate-error]');
    const button = this.querySelector('[data-gate] button');
    error.hidden = true;
    button.disabled = true;
    try {
      if (!this.worker) throw new Error(`Wholesale ordering isn't switched on yet. Please email ${this.dataset.contact}.`);
      const res = await fetch(`${this.worker}/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: input.value }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.token) throw new Error(data.error || "That password didn't work.");
      store.set(TOKEN_KEY, data.token);
      input.value = '';
      this.showSheet();
    } catch (e) {
      error.textContent = e.message === 'Failed to fetch' ? 'We couldn’t reach the wholesale system. Please try again.' : e.message;
      error.hidden = false;
    } finally {
      button.disabled = false;
    }
  }

  lock(message) {
    store.remove(TOKEN_KEY);
    this.querySelector('[data-unlocked]').hidden = true;
    this.querySelector('[data-gate]').hidden = false;
    const error = this.querySelector('[data-gate-error]');
    error.textContent = message || '';
    error.hidden = !message;
  }

  // Free set promo (first 5 shops): ask the worker how many are left, then mention it in the hint.
  async loadPromo() {
    try {
      const res = await fetch(`${this.worker}/promo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: store.get(TOKEN_KEY) }),
      });
      const data = await res.json();
      if (res.ok && data.freeSetsLeft > 0) {
        this.promo = data;
        this.update();
      }
    } catch {
      /* no promo line, checkout still works */
    }
  }

  showSheet() {
    this.querySelector('[data-gate]').hidden = true;
    this.querySelector('[data-unlocked]').hidden = false;
    if (this.ready) return;
    this.ready = true;
    this.renderTiers();
    this.renderSizes();
    this.bindExtras();
    this.querySelector('[data-checkout]').addEventListener('click', () => this.checkout());
    this.loadPromo();
    const bar = this.querySelector('.wholesale__summary');
    new ResizeObserver(() =>
      document.documentElement.style.setProperty('--wholesale-bar-height', `${bar.offsetHeight}px`)
    ).observe(bar);
    const shop = this.querySelector('[data-store-name]');
    shop.value = this.data.storeName || '';
    shop.addEventListener('input', () => this.update());
    const email = this.querySelector('[data-contact-email]');
    email.value = this.data.email || '';
    email.addEventListener('input', () => this.update());
    this.update();
  }

  // --- Sheet -----------------------------------------------------------------

  renderTiers() {
    const body = this.querySelector('[data-wholesale-tiers]');
    body.innerHTML = TIERS.map(
      (t) =>
        `<tr><td>${t.minSets}+</td><td>${money(t.piecePrice * 2)}</td><td>${money(t.piecePrice)}</td>` +
        `<td>${t.embroidery ? `$${t.embroidery}` : 'Free'}</td></tr>`
    ).join('');
  }

  renderSizes() {
    for (const product of this.data.products) {
      const wrap = this.querySelector(`[data-sizes="${product.key}"]`);
      wrap.innerHTML = '';
      for (const s of product.sizes) {
        const row = document.createElement('div');
        row.className = `wholesale__size${s.available ? '' : ' wholesale__size--soldout'}`;
        row.innerHTML = `<span class="wholesale__size-label">${s.size}</span>`;
        if (!s.available) {
          row.insertAdjacentHTML('beforeend', '<span class="wholesale__muted">Sold out</span>');
        } else {
          row.insertAdjacentHTML(
            'beforeend',
            `<div class="wholesale__stepper">
              <button type="button" aria-label="Fewer ${s.size}" data-step="-1">−</button>
              <input type="number" inputmode="numeric" min="0" ${s.max != null ? `max="${s.max}"` : ''} placeholder="0"
                aria-label="${product.title} size ${s.size} quantity">
              <button type="button" aria-label="More ${s.size}" data-step="1">+</button>
            </div>`
          );
          const input = row.querySelector('input');
          s.set = (n) => {
            const cap = s.max != null ? s.max : 500;
            const v = Math.max(0, Math.min(cap, Math.floor(Number(n)) || 0));
            this.qty[product.key][s.variantId] = v;
            input.value = v || '';
            this.update();
          };
          input.addEventListener('input', () => s.set(input.value));
          row.querySelectorAll('[data-step]').forEach((b) =>
            b.addEventListener('click', () => s.set((this.qty[product.key][s.variantId] || 0) + Number(b.dataset.step)))
          );
        }
        wrap.append(row);
      }
    }
  }

  bindExtras() {
    const box = this.querySelector('[data-embroidery]');
    if (!box) return;
    const fields = this.querySelector('[data-embroidery-fields]');
    box.addEventListener('change', () => {
      fields.hidden = !box.checked;
      this.update();
    });
    this.querySelector('[data-embroidery-notes]').addEventListener('input', () => this.update());
    const picker = this.querySelector('[data-embroidery-files]');
    picker.addEventListener('change', () => {
      const all = [...picker.files];
      const tooBig = all.filter((f) => f.size > MAX_FILE_MB * 1024 * 1024);
      this.files = all.filter((f) => f.size <= MAX_FILE_MB * 1024 * 1024).slice(0, MAX_FILES);
      const list = this.querySelector('[data-file-list]');
      list.replaceChildren(...this.files.map((f) => Object.assign(document.createElement('li'), { textContent: f.name })));
      if (all.length - tooBig.length > MAX_FILES) list.append(Object.assign(document.createElement('li'), { textContent: `Only the first ${MAX_FILES} files will be sent.` }));
      if (tooBig.length) list.append(Object.assign(document.createElement('li'), { textContent: `Skipped (over ${MAX_FILE_MB} MB): ${tooBig.map((f) => f.name).join(', ')}` }));
    });
  }

  get embroideryOn() {
    return !!this.querySelector('[data-embroidery]')?.checked;
  }

  update() {
    const q = quote({ tops: this.qty.tops, bottoms: this.qty.bottoms, embroidery: this.embroideryOn });
    this.q = q;
    const out = (k) => this.querySelector(`[data-out="${k}"]`);
    out('sets').textContent = q.sets % 1 ? q.sets.toFixed(1) : q.sets;
    // Below the minimum, show a running total at the 5-set price so prices update from the first piece.
    out('set-price').textContent = money(q.setPrice);
    out('total').textContent = money(q.total);

    let hint;
    if (q.pieces === 0) hint = 'Start with at least 5 tops and 5 bottoms (any sizes).';
    else if (!q.meetsMinimum) {
      const need = [
        q.needTops && `${q.needTops} more top${q.needTops > 1 ? 's' : ''}`,
        q.needBottoms && `${q.needBottoms} more bottom${q.needBottoms > 1 ? 's' : ''}`,
      ].filter(Boolean);
      hint = `Add ${need.join(' and ')} to reach the 5-set minimum.`;
    } else if (q.nextTier) {
      const n = q.setsToNextTier;
      hint = `Extra tops or bottoms are ${money(q.piecePrice)} each. Add ${n} more set${n > 1 ? 's' : ''} (${n * 2} pieces) for ${money(q.nextTier.piecePrice * 2)}/set.`;
    } else hint = `Extra tops or bottoms are ${money(q.piecePrice)} each. You're at our best price.`;
    if (q.tier && this.promo) {
      hint += q.sets >= this.promo.minSets
        ? ' Your extra set is on us: code EXTRASET is added at checkout.'
        : ` Order ${this.promo.minSets} sets and one is on us (first 5 shops).`;
    }
    if (q.tier && q.embroideryTotal) hint += ` Includes ${money(q.embroideryTotal)} embroidery.`;
    if (q.tier) hint += ' Free standard shipping.';
    out('hint').textContent = hint;

    const embPrice = this.querySelector('[data-embroidery-price]');
    if (embPrice) {
      embPrice.textContent = q.tier
        ? `(${q.tier.embroidery ? `${money(q.tier.embroidery)} per top` : 'free at this tier'})`
        : '(priced by tier)';
    }

    const notesOk = !this.embroideryOn || this.querySelector('[data-embroidery-notes]').value.trim();
    const shopOk = this.querySelector('[data-store-name]').value.trim();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.querySelector('[data-contact-email]').value.trim());
    this.querySelector('[data-checkout]').disabled = !q.tier || !notesOk || !shopOk || !emailOk || this.busy;

    // Once the order meets the minimum, flag any required field that's still empty in red.
    const flag = (selector, missing) =>
      this.querySelector(selector)?.closest('.wholesale__field')?.classList.toggle('is-missing', !!(q.tier && missing));
    flag('[data-store-name]', !shopOk);
    flag('[data-contact-email]', !emailOk);
    flag('[data-embroidery-notes]', !notesOk);
    if (!this.busy) {
      const missing = [!shopOk && 'your shop name', !emailOk && 'a contact email', !notesOk && 'embroidery details'].filter(Boolean);
      const last = missing.length > 1 ? `${missing.slice(0, -1).join(', ')} and ${missing.at(-1)}` : missing[0];
      this.showError(q.tier && missing.length ? `Add ${last} to check out.` : '');
    }
  }

  showError(msg) {
    const el = this.querySelector('[data-out="error"]');
    el.textContent = msg;
    el.hidden = !msg;
  }

  // --- Checkout --------------------------------------------------------------

  async checkout() {
    const q = this.q;
    if (!q?.tier) return;
    const button = this.querySelector('[data-checkout]');
    this.busy = true;
    button.textContent = 'Opening checkout…';
    this.update();
    this.showError('');

    try {
      const shopName = this.querySelector('[data-store-name]').value.trim().slice(0, 80);
      const contactEmail = this.querySelector('[data-contact-email]').value.trim().slice(0, 200);
      const notes = this.querySelector('[data-embroidery-notes]')?.value.trim().slice(0, 1000) || '';

      // 1. One-time wholesale codes for this order (the worker re-checks the tier).
      const res = await fetch(`${this.worker}/codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: store.get(TOKEN_KEY), tops: this.qty.tops, bottoms: this.qty.bottoms, store: shopName }),
      });
      const codes = await res.json().catch(() => ({}));
      if (res.status === 401) return this.lock(codes.error || 'Please enter the wholesale password again.');
      if (!res.ok || !codes.priceCode) throw new Error(codes.error || `Wholesale checkout isn't available right now. Please email ${this.dataset.contact}.`);

      // 2. Build the cart.
      const items = [];
      for (const key of ['tops', 'bottoms']) {
        for (const [variantId, quantity] of Object.entries(this.qty[key])) {
          if (quantity > 0) items.push({ id: Number(variantId), quantity });
        }
      }
      await cartFetch('cart/clear.js', { method: 'POST' });
      await cartFetch('cart/add.js', { method: 'POST', body: JSON.stringify({ items }) });

      if (this.embroideryOn && q.tops > 0) {
        const variant = this.data.embroidery.find((v) => v.title === `${q.tier.minSets} sets`);
        if (!variant) throw new Error(`Embroidery can't be added right now. Please email ${this.dataset.contact}.`);
        // Artwork uploads ride on the embroidery line as line item properties; links show on the order.
        const form = new FormData();
        form.append('id', variant.variantId);
        form.append('quantity', q.tops);
        form.append('properties[Embroidery request]', notes);
        this.files.forEach((file, i) => form.append(`properties[Artwork ${i + 1}]`, file, file.name));
        await cartFetch('cart/add.js', { method: 'POST', body: form });
      }

      const note = ['Wholesale order', `Shop: ${shopName}`, `Contact email: ${contactEmail}`, this.embroideryOn && `Embroidery on ${q.tops} tops: ${notes}`]
        .filter(Boolean)
        .join('\n');
      const cart = await cartFetch('cart/update.js', {
        method: 'POST',
        body: JSON.stringify({
          note,
          attributes: { 'Wholesale order': 'Yes', 'Wholesale tier': `${q.tier.minSets} sets`, 'Store name': shopName, 'Contact email': contactEmail },
          discount: [codes.priceCode, codes.freeSetCode].filter(Boolean).join(','),
        }),
      });

      // 3. Safety check: never send a retailer to checkout at the wrong price.
      const ids = new Set(this.data.products.flatMap((p) => p.sizes.map((s) => s.variantId)));
      const charged = cart.items.filter((i) => ids.has(i.variant_id)).reduce((a, i) => a + i.final_line_price, 0) / 100;
      if (Math.abs(charged - q.piecesSubtotal) > 0.01) {
        console.error('Wholesale price mismatch', { expected: q.piecesSubtotal, charged, discounts: cart.discount_codes });
        throw new Error(`Wholesale pricing isn't lining up right now. Please email ${this.dataset.contact} and we'll sort it out.`);
      }

      // Pre-fill Shopify checkout with the retailer's email and shop name (company).
      const params = new URLSearchParams({
        'checkout[email]': contactEmail,
        'checkout[shipping_address][company]': shopName,
      });
      window.location.href = `${root()}checkout?${params}`;
    } catch (e) {
      this.busy = false;
      button.textContent = 'Checkout';
      this.update();
      this.showError(e.message === 'Failed to fetch' ? 'We couldn’t reach the wholesale system. Please try again.' : e.message || 'Something went wrong. Please try again.');
    }
  }
}

if (!customElements.get('wholesale-order')) customElements.define('wholesale-order', WholesaleOrder);
